import cookies from 'js-cookie';
import { get, findIndex, flowRight, noop } from 'lodash';
import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { IM_PAYING_MYSELF } from '@zocdoc-frontend-common/components-insuranceform';
import { recordAssignment } from '@zocdoc-frontend-common/utils-ab';
import {
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { PCE_STATIC_WITH_NEW_EXISTING_VALIDATION } from 'src/config/ab/experiments';
import {
    navigateToNewExistingVerification,
    clearNewExistingVerification,
} from 'src/config/router/browserHistory';
import { shouldUseProductionSettings } from 'src/utils/environment';
import {
    APPOINTMENT_TIME,
    USING_INSURANCE_WARNING,
    INSURANCE_NOT_TAKEN_WARNING,
    PHONE_REQUIRED,
} from '../../components/AppointmentRequirements/constants';
import GovernmentInsuranceModal from '../../components/GovernmentInsuranceModal';
import { BOOKING_STATUS, BookingStateProperties } from '../../constants';
import { insuranceValidForCheckingAcceptance } from '../../utils';
import { getReferrerType } from '../../utils/getReferrerType';
import { REFERRER_TYPES_EXCLUDED_FROM_REDIRECT_TO_PATIENT_HOME } from './constants';

// Cookie from profile to denote whether they explicitly selected a value for new/existing
const DID_SELECT_NEW_EXISTING_FROM_PROFILE =
    'SelectedNewExistingPatientOnProfile';

/**
 * @param {{
 *     bookingId: string,
 * }} params
 * @param {boolean} receivedSchedulingGovernmentInsuranceError
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @returns {JSX.Element}
 * @constructor
 */
export class ReviewAndBookPageStateManagerWithoutMetrics extends Component {
    static defaultProps = {
        receivedSchedulingGovernmentInsuranceError: false,
        sendMetric: noop,
    };

    constructor(props) {
        super(props);
        const { showStaticReviewAndBook } = this.props;

        // static review in book for users who are eligible and in experiment
        const shouldShowStaticReviewAndBook =
            showStaticReviewAndBook && this.isEligibleForStaticReviewAndBook();
        const showNewExistingVerification =
            shouldShowStaticReviewAndBook &&
            this.shouldShowNewExistingVerification();
        this.state = {
            disableButton: false,
            hasAppointmentTimeError: false,
            shouldShowStaticReviewAndBook,
            showNewExistingVerification,
            showNewSubpatientForm: false,
            showPhoneVerificationForm: false,
            showRescheduleTimeSelectionForm: true,
            showGovernmentInsuranceModal: false,
            shouldShowInNetworkOnlyWarning: false,
        };
    }

    isEligibleForStaticReviewAndBook = () => {
        const requirements = get(
            this.props.bookingState,
            BookingStateProperties.requirements,
            []
        );

        const hasDisqualifyingRequirements = requirements.some((req) => {
            return (
                req.key !== USING_INSURANCE_WARNING &&
                req.key !== INSURANCE_NOT_TAKEN_WARNING &&
                req.key !== PHONE_REQUIRED &&
                req.key !== APPOINTMENT_TIME
            );
        });

        return (
            this.props.bookingState &&
            !hasDisqualifyingRequirements &&
            !this.isReschedule()
        );
    };

    shouldShowNewExistingVerification = () => {
        const { bookingState, providerId } = this.props;
        const cookiedProviderId = cookies.get(
            DID_SELECT_NEW_EXISTING_FROM_PROFILE,
            null
        );

        // They explicitly selected new/existing earlier, so don't show the verification step
        const didSelectNewExisting =
            cookiedProviderId && cookiedProviderId === providerId;
        if (!didSelectNewExisting && bookingState) {
            // If they have booked with this provider before, don't show verification
            return !get(
                bookingState,
                BookingStateProperties.hasBookedProviderBefore,
                false
            );
        }

        return false;
    };

    isReschedule = () => {
        const { bookingState } = this.props;

        return !!get(
            bookingState,
            BookingStateProperties.rescheduleRequestId,
            null
        );
    };

    hasUsingInsuranceRequirement = () => {
        const requirements = get(
            this.props.bookingState,
            BookingStateProperties.requirements,
            []
        );

        return (requirements || []).some(
            (requirement) => requirement.key === USING_INSURANCE_WARNING
        );
    };

    handleNewPatientClick = () => {
        this.props.trackClickNewSubpatient();
        this.setState({ showNewSubpatientForm: true });
    };

    handleClickCancelModal = () => {
        if (this.state.showNewSubpatientForm) {
            this.props.trackClickExitNewSubpatientModal();
        }
        if (
            this.state.showRescheduleTimeSelectionForm &&
            get(
                this.props.bookingState,
                BookingStateProperties.rescheduleRequestId,
                null
            )
        ) {
            this.props.trackClickExitRescheduleModal();
        }
        if (this.state.showPhoneVerificationForm) {
            this.props.resetPhoneVerificationForm();
            this.props.trackClickExitPhoneVerificationModal();
        }
        this.setState({
            showNewSubpatientForm: false,
            showPhoneVerificationForm: false,
            showRescheduleTimeSelectionForm: false,
        });
    };

    handleRescheduleClick = () => {
        this.props.trackFinishedRescheduleModal();
        this.setState({
            showRescheduleTimeSelectionForm: false,
        });
    };

    handleOnSubpatientCreated = (subpatientId) => {
        const {
            selectPatient,
            resetNewSubpatientForm,
            updateExistingPatientState,
        } = this.props;

        this.setState({ showNewSubpatientForm: false });
        selectPatient.onSubpatientCreated(subpatientId);
        updateExistingPatientState(false);
        resetNewSubpatientForm();
    };

    handleBookAppointmentClick = () => {
        const {
            hasTimeslotConstraint,
            showResourceConstraintModal,
            appointmentInfo,
            receivedSchedulingGovernmentInsuranceError,
            inNetworkOnlyBookableText,
        } = this.props;

        if (hasTimeslotConstraint || inNetworkOnlyBookableText) {
            if (hasTimeslotConstraint) {
                showResourceConstraintModal({
                    professionalName: appointmentInfo.professionalName,
                    bookAppointment: () => this.bookAppointment(),
                    changeTimeslot: () => this.showTimesgridTooltip(),
                });
            }
            if (inNetworkOnlyBookableText) {
                this.setState({ shouldShowInNetworkOnlyWarning: true });
            }
        } else if (!appointmentInfo.hasAppointmentTimeError) {
            this.bookAppointment();
        } else if (receivedSchedulingGovernmentInsuranceError) {
            this.setState({ showGovernmentInsuranceModal: true });
        }

        // Disable button for 5 seconds
        this.setState({ disableButton: true });
        setTimeout(this.reenableBookAppointmentButton, 5000);
    };

    reenableBookAppointmentButton = () => {
        this.setState({ disableButton: false });
    };

    bookAppointment = () => {
        const {
            onBookAppointmentClick,
            selectPatient,
            bookingState,
            appointmentDetails,
            selectedInsuranceCard,
        } = this.props;
        const insuranceId = appointmentDetails.insuranceId;
        const imgSrc = selectedInsuranceCard?.insuranceCardImageUrls?.front;
        selectPatient.onContinue();
        onBookAppointmentClick(bookingState, insuranceId, imgSrc);
    };

    showPhoneVerificationForm = () => {
        this.setState({ showPhoneVerificationForm: true });
    };

    onPhoneVerified = () => {
        this.props.onPhoneVerified();
        this.setState({ showPhoneVerificationForm: false });
    };

    componentDidMount() {
        const {
            bookingState,
            fetchInsuranceInfo,
            resetPhoneVerificationForm,
            trackExposedControls,
        } = this.props;
        this.props.fetchServiceTypes();

        const insuranceCarrierId = get(
            bookingState,
            BookingStateProperties.insuranceCarrierId,
            IM_PAYING_MYSELF
        );
        const insurancePlanId = get(
            bookingState,
            BookingStateProperties.insurancePlanId,
            '0'
        );
        if (
            parseInt(insuranceCarrierId, 10) > 0 &&
            parseInt(insurancePlanId, 10) > 0
        ) {
            fetchInsuranceInfo(insuranceCarrierId, insurancePlanId);
        }

        resetPhoneVerificationForm();

        // Track initial state of exposed controls
        // BUG: cannot use isFeePerBooking here because page remounts to inject
        // that prop. Need to filter in optimizely query for WACOGA states.
        trackExposedControls();

        if (this.isEligibleForStaticReviewAndBook()) {
            recordAssignment(PCE_STATIC_WITH_NEW_EXISTING_VALIDATION);
        }

        if (this.state.showNewExistingVerification) {
            // navigate to a hash url for new/existing verification
            navigateToNewExistingVerification();
            // If user goes back, capture and reshow new/existing verification
            window.addEventListener('popstate', this.handleWindowStateChange);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleWindowStateChange);
        clearTimeout(this.reenableBookAppointmentButton);
    }

    componentDidUpdate(prevProps, prevState) {
        this.checkIfInsuranceUpdated(prevProps);

        const {
            receivedSchedulingGovernmentInsuranceError:
                previousShouldShowGovernmentInsuranceModal,
            inNetworkOnlyBookableText: prevInNetworkOnlyBookableText,
        } = prevProps;
        const {
            receivedSchedulingGovernmentInsuranceError:
                currentShouldShowGovernmentInsuranceModal,
            inNetworkOnlyBookableText,
        } = this.props;

        const { shouldShowStaticReviewAndBook } = this.state;

        if (
            previousShouldShowGovernmentInsuranceModal !==
            currentShouldShowGovernmentInsuranceModal
        ) {
            this.setState({
                showGovernmentInsuranceModal:
                    currentShouldShowGovernmentInsuranceModal,
            });
        }

        if (
            shouldShowStaticReviewAndBook &&
            !this.isEligibleForStaticReviewAndBook()
        ) {
            // On requirements update, puntback
            // double render :-(
            this.setState({ shouldShowStaticReviewAndBook: false });
        }

        if (
            prevInNetworkOnlyBookableText &&
            !Boolean(inNetworkOnlyBookableText)
        ) {
            this.setState({ shouldShowInNetworkOnlyWarning: false });
        }
    }

    pollBookingState = () => {
        const { getBookingState } = this.props.appointmentDetails;
        this.timeout = setTimeout(() => {
            getBookingState(this.props.params.bookingId, true);
            this.pollBookingState();
        }, this.props.pollingDelay);
    };

    bookingDidNotSucceed = (nextProps) => {
        return (
            !nextProps.inBetweenUpdateAndSubmit &&
            nextProps.localInSync &&
            (nextProps.bookingStatus === BOOKING_STATUS.FAILED ||
                nextProps.bookingStatus === BOOKING_STATUS.IN_PROGRESS ||
                nextProps.submitBookingStateFailure)
        );
    };

    handleWindowStateChange = (e) => {
        const { shouldShowStaticReviewAndBook, showNewExistingVerification } =
            this.state;
        if (e.state && shouldShowStaticReviewAndBook) {
            // show new existing verification again
            this.setState({
                showNewExistingVerification: !showNewExistingVerification,
            });
        }
    };

    showTimesgridTooltip = () => {
        this.setState({
            hasAppointmentTimeError: true,
            shouldShowStaticReviewAndBook: false,
        });
    };

    checkIfInsuranceUpdated = (prevProps) => {
        const {
            bookingState,
            appointmentDetails: {
                gettingInsuranceCarrierName,
                gettingInsurancePlanName,
            },
        } = this.props;
        if (!gettingInsuranceCarrierName && !gettingInsurancePlanName) {
            const prevInsuranceCarrierId = get(
                prevProps.bookingState,
                BookingStateProperties.insuranceCarrierId,
                IM_PAYING_MYSELF
            );
            const prevInsurancePlanId = get(
                prevProps.bookingState,
                BookingStateProperties.insurancePlanId,
                '0'
            );

            const insuranceCarrierId = get(
                bookingState,
                BookingStateProperties.insuranceCarrierId,
                IM_PAYING_MYSELF
            );

            const insurancePlanId = get(
                bookingState,
                BookingStateProperties.insurancePlanId,
                '0'
            );

            if (
                (prevInsuranceCarrierId !== insuranceCarrierId ||
                    prevInsurancePlanId !== insurancePlanId) &&
                insuranceValidForCheckingAcceptance(this.props.bookingState)
            ) {
                // TODO: we are fetching insurance information everytime we update insurance.
                // we should not have to do this; we should be able to extract the names
                // into our store
                this.props.fetchInsuranceInfo(
                    insuranceCarrierId,
                    insurancePlanId
                );
            }
        }
    };

    async componentWillReceiveProps(nextProps) {
        const {
            logDidHideSyncOverlay,
            logDidShowSyncOverlay,
            onBookingFailure,
            onBookingSuccess,
            providerLocationPhone,
            submittingBookingState: wasSubmittingBookingState,
        } = this.props.appointmentDetails;

        const {
            bookingErrorCode,
            bookingErrors,
            bookingState,
            bookingStatus,
            submittingBookingState: isSubmittingBookingState,
        } = nextProps.appointmentDetails;

        const {
            sendMetric,
            shouldShowLoadingAnimation: wasShowingLoadingAnimation,
        } = this.props;
        const { shouldShowLoadingAnimation: isShowingLoadingAnimation } =
            nextProps;
        if (!wasShowingLoadingAnimation && isShowingLoadingAnimation) {
            logDidShowSyncOverlay();
        }

        if (isShowingLoadingAnimation) {
            //We want to show the sync overlay for 10 seconds at a miniumum in production.
            const MIN_TIME_TO_SHOW_SYNC_OVERLAY_MS =
                shouldUseProductionSettings() ? 10000 : 3000;
            var startOverlaySpeedBump = new Date().getTime();
            if (this.bookingDidNotSucceed(nextProps.appointmentDetails)) {
                if (this.state.shouldShowStaticReviewAndBook) {
                    sendMetric({
                        action: 'Hide Static Review and Book - Submission Error',
                        element: 'ReviewAndBook',
                        initiator: EVENT_INITIATORS.USER,
                        interactionType: INTERACTION_TYPES.VIEW,
                    });
                    this.setState({
                        shouldShowStaticReviewAndBook: false,
                    });
                }

                clearTimeout(this.timeout);
                this.props.hideLoadingAnimation();
                logDidHideSyncOverlay();

                onBookingFailure({
                    bookingErrorCode,
                    bookingErrors,
                    bookingState,
                    bookingStatus,
                    providerLocationPhone,
                });
            } else if (bookingStatus === BOOKING_STATUS.PENDING) {
                if (wasSubmittingBookingState && !isSubmittingBookingState) {
                    clearTimeout(this.timeout);
                    this.pollBookingState();
                }
            } else if (
                bookingStatus === BOOKING_STATUS.COMPLETE &&
                bookingStatus !== this.props.appointmentDetails.bookingStatus
            ) {
                clearTimeout(this.timeout);
                var currentMillisecondsPassed =
                    new Date().getTime() - startOverlaySpeedBump;

                if (
                    currentMillisecondsPassed > MIN_TIME_TO_SHOW_SYNC_OVERLAY_MS
                ) {
                    onBookingSuccess(this.eligibleToRedirectToPatientHome());
                } else {
                    const timeToWait =
                        MIN_TIME_TO_SHOW_SYNC_OVERLAY_MS -
                        currentMillisecondsPassed;
                    setTimeout(() => {
                        onBookingSuccess(
                            this.eligibleToRedirectToPatientHome()
                        );
                    }, timeToWait);
                }
            }
        }
    }

    eligibleToRedirectToPatientHome = () => {
        const referrerType = getReferrerType();
        const shouldSeeBookingComplete =
            typeof referrerType === 'string' &&
            findIndex(
                REFERRER_TYPES_EXCLUDED_FROM_REDIRECT_TO_PATIENT_HOME,
                (r) => r === referrerType.toLowerCase()
            ) !== -1;

        return !shouldSeeBookingComplete;
    };

    closeGovernmentInsuranceModal = () =>
        this.setState({ showGovernmentInsuranceModal: false });

    maybeRenderGovernmentInsuranceWarning = () => {
        const { showGovernmentInsuranceModal } = this.state;
        const { providerId, locationId } = this.props;

        if (showGovernmentInsuranceModal) {
            return (
                <GovernmentInsuranceModal
                    data-test="government-insurance-modal"
                    onCancelClick={this.closeGovernmentInsuranceModal}
                    providerId={providerId}
                    locationId={locationId}
                />
            );
        }
    };

    handleNewExistingContinue = () => {
        clearNewExistingVerification();
        this.setState({ showNewExistingVerification: false });
    };

    render() {
        const {
            appointmentDetails,
            appointmentInfo,
            availableInsuranceCardDataId,
            bookingState,
            children,
            continueWasClicked,
            coronaSymptomsText,
            coronaContactsText,
            coronaLocationText,
            updateCoronaRequirementsValues,
            directoryId,
            dispatchPageLoadTime,
            insuranceCarrierName,
            insurancePlanName,
            phoneNumber,
            phoneNumberRequired,
            selectPatient,
            shouldShowLoadingAnimation,
            showInsuranceError,
            showPayForSelfError,
            showMissingFieldsError,
            shouldShowCoronaErrorPage,
            trackViewedRescheduleModal,
            trackGtmContainerLoad,
            updatingIsExistingPatient,
            location,
            receivedSchedulingGovernmentInsuranceError,
            showLifeStanceSection,
            shouldShowNewPostBookingSurveyAbFlag,
            inNetworkOnlyBookableText,
        } = this.props;

        const {
            disableButton,
            shouldShowStaticReviewAndBook,
            showNewExistingVerification,
            showNewSubpatientForm,
            showPhoneVerificationForm,
            showRescheduleTimeSelectionForm,
            shouldShowInNetworkOnlyWarning,
        } = this.state;

        const isReschedule = this.isReschedule();

        const childProps = {
            ...selectPatient,
            ...appointmentInfo,
            appointmentDetails,
            availableInsuranceCardDataId,
            coronaSymptomsText,
            coronaContactsText,
            coronaLocationText,
            directoryId,
            disableButton,
            disableContinue:
                appointmentInfo.disableContinue ||
                selectPatient.disableContinue ||
                appointmentDetails.disableContinue,
            dispatchPageLoadTime,
            hasUsingInsuranceRequirement: this.hasUsingInsuranceRequirement(),
            inNetworkOnlyBookableText,
            shouldShowInNetworkOnlyWarning,
            insuranceCardDataId: get(
                bookingState,
                BookingStateProperties.insuranceCardDataId,
                null
            ),
            insuranceCarrierName,
            insurancePlanName,
            isInsuranceCardShared: get(
                bookingState,
                BookingStateProperties.isInsuranceCardShared,
                ''
            ),
            isReschedule,
            onAddOrEditPhoneNumberClick: this.showPhoneVerificationForm,
            onBookAppointmentClick: this.handleBookAppointmentClick,
            onClickCancelModal: this.handleClickCancelModal,
            onNewPatientClick: this.handleNewPatientClick,
            onPhoneVerified: this.onPhoneVerified,
            onSubpatientCreated: this.handleOnSubpatientCreated,
            phoneInputBlockHasError: phoneNumberRequired && continueWasClicked,
            phoneNumber,
            phoneNumberRequired,
            selectedPatient: get(
                bookingState,
                BookingStateProperties.patientId,
                ''
            ).toString(),
            shouldShowLoadingAnimation,
            shouldShowStaticReviewAndBook,
            showRescheduleTimeSelectionForm:
                isReschedule && showRescheduleTimeSelectionForm,
            onRescheduleClick: this.handleRescheduleClick,
            showInsuranceError,
            showPayForSelfError,
            showMissingFieldsError,
            showNewSubpatientForm,
            showPhoneVerificationForm,
            showNewExistingVerification,
            shouldShowCoronaErrorPage,
            onContinueNewExistingVerifiation: this.handleNewExistingContinue,
            trackViewedRescheduleModal,
            trackGtmContainerLoad,
            hasAppointmentTimeError:
                this.state.hasAppointmentTimeError ||
                appointmentInfo.hasAppointmentTimeError,
            updatingIsExistingPatient,
            updateCoronaRequirementsValues,
            location,
            receivedSchedulingGovernmentInsuranceError,
            showLifeStanceSection,
            shouldShowNewPostBookingSurveyAbFlag,
            onScanInsuranceCardImagesSuccess:
                this.props.onScanInsuranceCardImagesSuccess,
            setScannedCardScannerOpenState:
                this.props.setScannedCardScannerOpenState,
            setScannedCardLoadingState: this.props.setScannedCardLoadingState,
        };

        const childrenWithProps = React.Children.map(children, (child) =>
            React.cloneElement(child, {
                ...childProps,
            })
        );

        return (
            <div>
                {this.maybeRenderGovernmentInsuranceWarning()}
                {childrenWithProps}
            </div>
        );
    }
}

export default flowRight(
    withRouter,
    withCustomMetrics({ section: 'ReviewAndBook', component: 'ReviewAndBook' })
)(ReviewAndBookPageStateManagerWithoutMetrics);
